/**
 * useStrategyThemeSettings - Hook for loading V2 theme settings from a strategy.
 *
 * Reads from strategies/{strategyId}/themeSettingsV2/settings subcollection.
 * Pattern mirrors useOnboardingThemeSettingsV2 but uses StrategyService.
 *
 * @module themes/hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';

export interface UseStrategyThemeSettingsOptions {
  projectId: string;
  strategyId: string;
  skipInitialLoad?: boolean;
}

export interface UseStrategyThemeSettingsReturn {
  /** V2 Theme settings (null if not loaded or not set) */
  themeSettings: OnboardingThemeSettingsV2 | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Refresh theme settings */
  refresh: () => Promise<void>;
  /** Update theme settings */
  update: (settings: OnboardingThemeSettingsV2) => Promise<void>;
}

/**
 * Hook for loading strategy-level V2 theme settings.
 */
export function useStrategyThemeSettings(
  options: UseStrategyThemeSettingsOptions
): UseStrategyThemeSettingsReturn {
  const { projectId, strategyId, skipInitialLoad = false } = options;

  const [themeSettings, setThemeSettings] = useState<OnboardingThemeSettingsV2 | null>(null);
  const [loading, setLoading] = useState(!skipInitialLoad);
  const [error, setError] = useState<string | null>(null);

  const serviceRef = useRef(new StrategyService(projectId));

  // Update service when projectId changes
  useEffect(() => {
    serviceRef.current = new StrategyService(projectId);
  }, [projectId]);

  const load = useCallback(async () => {
    if (!projectId || !strategyId) {
      setThemeSettings(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const settings = await serviceRef.current.getThemeSettingsV2(strategyId);
      setThemeSettings(settings);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load strategy theme settings';
      setError(message);
      setThemeSettings(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, strategyId]);

  const update = useCallback(
    async (settings: OnboardingThemeSettingsV2): Promise<void> => {
      if (!projectId || !strategyId) {
        throw new Error('Missing projectId or strategyId');
      }

      setLoading(true);
      setError(null);

      try {
        await serviceRef.current.saveThemeSettingsV2(strategyId, settings);
        setThemeSettings(settings);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save strategy theme settings';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, strategyId]
  );

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  useEffect(() => {
    if (!skipInitialLoad) {
      load();
    }
  }, [load, skipInitialLoad]);

  return {
    themeSettings,
    loading,
    error,
    refresh,
    update,
  };
}
